import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import reactifyWC from 'reactify-wc';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { publishAuthStep, publishLang } from '@nbx/frontend-helpers/publish';
import { useTheme, useLang, useAuthAccountClaims } from '@nbx/frontend-helpers/subscribe';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import styles from './styles.module.scss';
import AppVersion from './AppVersion';

const NBXButton = reactifyWC('nbx-button');
const microapps = window.MICROFRONTENDS_CONFIG;

function MobileAccounts(props) {
  const LANGS = window.LANGUAGES;
  const [savedLang, setSavedLang] = React.useState(null);
  const [startingLang, setStartingLang] = React.useState(LANGS.en);
  const theme = useTheme();
  const activeLang = useLang();
  const accountClaims = useAuthAccountClaims();
  const { t } = useTranslation();

  useEffect(() => {
    const getLang = async () => {
      const langFromStorage = await NBXStorage.getItem('i18nextLng', { useLocalStorage: true });
      setSavedLang(langFromStorage);
    };
    getLang();
  }, []);

  useEffect(() => {
    setStartingLang(savedLang && LANGS[savedLang] ? savedLang : LANGS.en);
  }, [savedLang, LANGS]);

  const changeActiveLang = newActiveLang => {
    publishLang(newActiveLang);
  };

  const resetAuthStep = () => {
    props.toggleMenu();
    publishAuthStep(null);
  };

  const getAccountName = userAccount => {
    if (!userAccount || !props.user) return null;
    let accountName = '';
    if (typeof userAccount.name === 'string') accountName = userAccount.name;
    else if (typeof props.user.firstName === 'string') accountName = props.user.firstName;
    else accountName = props.user.email;
    return accountName;
  };

  function renderLoggedInMenu() {
    return (
      <>
        <div className={styles.mobileSectionAccounts}>
          {props.userAccounts?.map(userAccount => {
            const accountName = getAccountName(userAccount);
            const active = props.activeAccount === userAccount.id;

            const renderContents = () => {
              return (
                <div
                  className={classnames([styles.accountRowContents], {
                    [styles.activeAccountRowContents]: active
                  })}>
                  <nbx-avatar name={accountName} selected={active} size="small" />
                  <div className={styles.accountInfo}>
                    <nbx-text>{accountName}</nbx-text>
                    <nbx-text muted>
                      {userAccount.name
                        ? t('header_account_type_organization')
                        : t('header_account_type_individual')}
                    </nbx-text>
                  </div>
                </div>
              );
            };
            return (
              <button
                type="button"
                className={styles.accountRow}
                key={userAccount.id}
                onClick={
                  props.switchingAccount
                    ? () => {}
                    : () => props.switchAccount(props.activeAccount, userAccount.id)
                }>
                {renderContents()}
              </button>
            );
          })}
        </div>
        {window.platform === 'web' && (
          <div className={styles.mobileSection}>
            <nbx-text>{t('header_page_mode_dark')}</nbx-text>
            <nbx-toggle toggled={theme === 'dark'} onClick={props.toggleTheme} />
          </div>
        )}
        <div className={styles.mobileSection}>
          <Link
            to={microapps?.routes?.accountPreferences?.path}
            className={styles.rowLink}
            onClick={props.toggleMenu}>
            <nbx-text>
              {t('common_language')} ({activeLang?.toUpperCase()})
            </nbx-text>
            <span className={styles.arrow}>→</span>
          </Link>
        </div>
        <div className={styles.mobileSectionAuth}>
          <nbx-button primary full-width onClick={props.signOut}>
            {t('header_link_signout')}
          </nbx-button>
          {window.PLATFORM !== 'web' && accountClaims?.roles?.includes('test') && <AppVersion />}
        </div>
      </>
    );
  }

  // If starting language was EN, only show EN & NO
  // else, show EN & starting language
  function renderLangButtons(languages) {
    return languages.map(key => {
      return (
        <nbx-button onClick={() => changeActiveLang(key)} inverted={key === activeLang} key={key}>
          {LANGS[key].toUpperCase()}
        </nbx-button>
      );
    });
  }

  function renderLoggedOutMenu() {
    return (
      <>
        {window.platform === 'web' && (
          <div className={styles.mobileSection}>
            <nbx-text>{t('header_page_mode_dark')}</nbx-text>
            <nbx-toggle toggled={theme === 'dark'} onClick={props.toggleTheme} />
          </div>
        )}
        <div className={styles.mobileSection}>
          <nbx-text>{t('common_language')}</nbx-text>
          <div className={styles.langButtons}>
            {startingLang === 'en' && renderLangButtons(['en', 'no'])}
            {startingLang !== 'en' && renderLangButtons(['en', startingLang])}
          </div>
        </div>
        <div className={styles.mobileSectionAuth}>
          <NBXButton primary full-width to={microapps?.routes?.signup?.path} onClick={resetAuthStep}>
            {t('common_sign_up')}
          </NBXButton>
          <div className={styles.spacer} />
          <NBXButton primary full-width inverted to={microapps?.routes?.login?.path} onClick={resetAuthStep}>
            {t('common_log_in')}
          </NBXButton>
        </div>
      </>
    );
  }

  return (
    <div className={styles.accountsMenu}>
      {props.user !== null ? renderLoggedInMenu() : renderLoggedOutMenu()}
    </div>
  );
}

MobileAccounts.propTypes = {
  user: PropTypes.object,
  userAccounts: PropTypes.array,
  activeAccount: PropTypes.string,
  switchAccount: PropTypes.func.isRequired,
  switchingAccount: PropTypes.bool.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  toggleTheme: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired
};

MobileAccounts.defaultProps = {
  userAccounts: [],
  activeAccount: null,
  user: null
};

export default MobileAccounts;
